<template>
    <div
        id="hot-key-menu-list"
        v-clickoutside="clickoutside"
        :class="{
            'view-nav': true,
            'view-nav--collapse': collapse,
            'view-nav--fixed': inDualView
        }"
        flex-item="full"
        @click="open"
    >
        <template v-if="!collapse">
            <NavMenu :menuData="mainTabs" :patentId="patentId">
                <template #default="{ context }">
                    <span v-html="context.label" />
                    <span v-if="context.name == 'image' && patent.total_figure > 0">({{ patent.total_figure }})</span>
                    <span v-if="context.name == 'image' && patent.total_figure === 0">(0)</span>
                </template>
            </NavMenu>
            <p v-if="otherTabs.length" class="view-nav__split-line" />
            <NavMenu :menuData="otherTabs" :patentId="patentId" />
        </template>
        <PtIcon
            :icon="`${ collapse ? 'SolidArrowRight' : 'SolidArrowLeft'}`"
            class="view-nav__left-arrow"
            @click.stop="toggle"
        />
    </div>
</template>

<script>
import { Icon } from 'patsnap-biz';
import clickoutside from 'element-ui/src/utils/clickoutside';
import { pluginsMapByName } from '../../subview_plugins/install';
import NavMenu from '../../components/NavMenu';
import { usePermission } from '@/composables/use-permission';
import { usePatentBasic } from '@/composables/use-patent-basic';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';

export default {
    name: 'SideNav',
    components: {
        NavMenu,
        PtIcon: Icon,
    },
    directives: {
        clickoutside,
    },
    props: {
        patentId: {
            type: String,
            required: true,
        },
        closeWhenClickoutside: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const {
            permissions,
        } = usePermission();

        const {
            getPatentBase,
        } = usePatentBasic();

        return {
            permissions,
            getPatentBase,
        };
    },
    data() {
        return {
            patent: {},
            collapse: false,
        };
    },
    computed: {
        inDualView() {
            return !this.$route.meta.pluginName;
        },
        mainTabs() {
            const legalSubTabs = [
                {
                    name: 'legalStatus',
                    hasData: true,
                    hasPermission: true,
                    label: this.getLabelByName('legalStatus'),
                },
                {
                    name: 'litigation',
                    hasData: this.patent.HAS_LITIGATION,
                    hasPermission: this.permissions.litigation,
                    label: this.getLabelByName('litigation'),
                },
                {
                    name: 'rerexam',
                    hasData: this.patent.HAS_REREXAM,
                    hasPermission: this.permissions.reexamInvalid,
                    label: this.getLabelByName('rerexam'),
                },
                {
                    name: 'licensing',
                    hasData: this.patent.HAS_LICENSE,
                    hasPermission: this.permissions.licensing,
                    label: this.getLabelByName('licensing'),
                },
                {
                    name: 'pledge',
                    hasData: this.patent.HAS_PLEDGE,
                    hasPermission: this.permissions.pledge,
                    label: this.getLabelByName('pledge'),
                },
                {
                    name: 'fireWrapper',
                    hasData: this.patent.HAS_FILE_WRAPPER,
                    hasPermission: this.permissions.fileWrapper,
                    label: this.getLabelByName('fireWrapper'),
                },
                {
                    name: 'customs',
                    hasData: this.patent.HAS_CUSTOMS,
                    hasPermission: this.permissions.customsData,
                    label: this.getLabelByName('customs'),
                },
            ]
                .map(item => {
                    return { parentView: 'legal', path: item.path || pluginsMapByName.get(item.name).exclusiveRoutePath, ...item };
                });

            const legalSubTabsFiltered = legalSubTabs.filter(item => item.hasPermission);

            const casSubTabs = [
                {
                    name: 'patentPak',
                    hasData: this.patent.HAS_PATENT_PAK,
                    hasPermission: true,
                    label: this.getLabelByName('patentPak'),
                },
                {
                    name: 'concepts',
                    hasData: this.patent.HAS_CONCEPT,
                    hasPermission: true,
                    label: this.getLabelByName('concepts'),
                },
                {
                    name: 'substances',
                    hasData: this.patent.HAS_SUBSTANCE,
                    hasPermission: true,
                    label: this.getLabelByName('substances'),
                },
                {
                    name: 'formulations',
                    hasData: this.patent.HAS_FORMULATION,
                    hasPermission: true,
                    label: this.getLabelByName('formulations'),
                },
                {
                    name: 'reactions',
                    hasData: this.patent.HAS_REACTION,
                    hasPermission: true,
                    label: this.getLabelByName('reactions'),
                },

            ].map(item => {
                return { parentView: 'cas', path: item.path || pluginsMapByName.get(item.name).exclusiveRoutePath, ...item };
            });

            const casSubTabsFiltered = casSubTabs.filter(item => item.hasPermission);

            const mainTabs = [
                {
                    name: 'abst',
                    hasData: true,
                    hasPermission: true,
                    label: this.getLabelByName('abst'),
                },
                {
                    name: 'clms',
                    hasData: true,
                    hasPermission: true,
                    label: this.getLabelByName('clms'),
                },
                {
                    name: 'desc',
                    hasData: true,
                    hasPermission: true,
                    label: this.getLabelByName('desc'),
                },
                {
                    name: 'image',
                    hasData: true,
                    hasPermission: true,
                    label: this.getLabelByName('image'),
                },
                {
                    name: 'pdf',
                    hasData: true,
                    hasPermission: this.permissions.pdf,
                    label: this.getLabelByName('pdf'),
                },
                {
                    name: 'valuation',
                    hasData: this.patent.valuation,
                    hasPermission: this.permissions.patentValue,
                    parentView: 'valuation',
                    label: this.getLabelByName('valuation'),
                },
                {
                    name: 'legal',
                    path: '/legal/status',
                    hasData: true,
                    hasPermission: this.permissions.legal,
                    label: this.$t('patent-view.LegalInformation'),
                    parentView: 'legal',
                    children: legalSubTabsFiltered,
                },
                //chemical CAS相关微前端入口
                {
                    name: 'cas',
                    path: '/cas/patentPak',
                    hasData: true,
                    hasPermission: this.permissions.cas,
                    label: this.$t('CAS.CAS'),
                    parentView: 'cas',
                    children: casSubTabsFiltered,
                },
            ];
            return mainTabs
                .map(item => {
                    return { parentView: 'overview', path: item.path || pluginsMapByName.get(item.name).exclusiveRoutePath, ...item };
                })
                .filter(item => item.hasPermission);
        },
        otherTabs() {
            const citationSubTabs = [
                {
                    name: 'citation-patent',
                    hasData: true,
                    hasPermission: true,
                    label: this.getLabelByName('citation-patent'),
                },
                {
                    name: 'citation-nopatent',
                    hasData: this.patent.HAS_REFERENCE,
                    hasPermission: true,
                    label: this.getLabelByName('citation-nopatent'),
                },
            ].map(item => {
                return { parentView: 'citation', path: item.path || pluginsMapByName.get(item.name).exclusiveRoutePath, ...item };
            });
            const otherTabs = [
                {
                    path: '/citation/patent',
                    hasData: true,
                    hasPermission: this.permissions.citation,
                    label: this.$t('patent-view.Citation'),
                    children: citationSubTabs,
                },
                {
                    name: 'patent-family',
                    hasData: true,
                    hasPermission: this.permissions.family,
                    parentView: 'patent-family',
                    label: this.getLabelByName('patent-family'),
                },
                {
                    name: 'similar',
                    hasData: true,
                    hasPermission: this.permissions.mlt,
                    label: this.getLabelByName('similar'),
                },
                {
                    name: 'literature',
                    hasData: true,
                    hasPermission: this.permissions.paper,
                    label: this.getLabelByName('literature'),
                },
            ].map(item => {
                return { path: item.path || pluginsMapByName.get(item.name).exclusiveRoutePath, ...item };
            });
            return otherTabs.filter(item => item.hasPermission);
        },
    },
    watch: {
        inDualView: {
            handler(bool) {
                this.collapse = bool;
            },
            immediate: true,
        },
    },
    async created() {
        this.patent = await this.getPatentBase(this.patentId);
    },
    methods: {
        getLabelByName(name) {
            return this.$t((pluginsMapByName.get(name) || {}).label);
        },
        toggle() {
            this.collapse = !this.collapse;
        },
        open() {
            if (this.collapse) {
                this.collapse = false;
            }
        },
        close() {
            if (!this.collapse) {
                this.collapse = true;
            }
        },
        clickoutside() {
            if (this.closeWhenClickoutside) {
                this.close();
            }
        },
    },
};
</script>

<style lang="scss">
.view-nav {
    position: relative;
    width: $leftBarWidth;
    min-width: $leftBarWidth;
    border-right: 1px solid $neutral-blue-40;
    background-color: $neutral-blue-20;
    padding: 16px 12px 0 12px;
    height: 100%;
    overflow: hidden;
    &--fixed {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 10;
        height: 100%;
        &.view-nav--collapse {
            position: absolute;
            left: 0;
        }
    }
    &--collapse {
        width: 27px;
        min-width: 27px;
        cursor: pointer;
        cursor: e-resize;
        .view-nav__left-arrow {
            left: 2px;
            fill: $status-info-primary;
            font-size: 20px;
        }
    }
    &__split-line {
        margin: 20px 0 20px -8px;
        width: calc(100% + 16px);
        height: 1px;
        background-color: $neutral-blue-40;
    }
    &__left-arrow {
        position: fixed;
        top: 150px;
        left: 144px - 11px/* 11px 是 icon 的宽度 */;
        padding: 2px;
        border-radius: 50%;
        border: 1px solid $neutral-blue-60;
        background-color: $white;
        outline: none;
        z-index: 6;
        cursor: pointer;
        &:hover {
            box-shadow: 1px 1px 2px #dedede;
            transform: scale(1.02);
        }
    }
}

.patent-app__content.dual-view {
    padding-left: 27px;
}
</style>
